import { AppColors, Loading, Section, useObservable, usePageTitle } from '@lib/common';
import { Container, Paper, Tab, Tabs } from '@material-ui/core';
import { AuditLogs, BreadcrumbBackButton } from 'app/shared';
import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useGetEmployees, usePollInvoiceRunById, usePollInvoiceStatuses } from '../hooks';
import {
  InvoiceRunDetails,
  InvoiceStatusFilterForm,
  InvoiceStatusList,
  SendInvoicesForm,
} from '../components';
import { InvoiceRunStatus, InvoiceSort, InvoiceStatusFilter } from 'app/state';
import { sessionQuery } from '@lib/state';
import { TabContext, TabPanel } from '@material-ui/lab';

export function InvoiceRunDetailsPage({
  match,
}: RouteComponentProps<{
  runId: string;
}>) {
  usePageTitle('Invoice Run Details');
  const runId = match.params.runId;
  const propertyId = useObservable(sessionQuery.propertyId, 'async');
  const [filters, setFilters] = useState<InvoiceStatusFilter>({ invoiceRunId: runId });
  const [tab, setTab] = useState('list');
  const [continuePolling, setContinuePolling] = useState(true);
  const { run } = usePollInvoiceRunById(runId, continuePolling);
  const { employees } = useGetEmployees(propertyId ?? '');
  const { statuses, corporateAccounts } = usePollInvoiceStatuses(filters, continuePolling);

  const onFilterChange = useCallback(
    (filters: InvoiceStatusFilter) => {
      setFilters({ ...filters, invoiceRunId: runId });
    },
    [runId, setFilters]
  );

  useEffect(() => {
    if (run?.generatingExports || run?.runStatus !== InvoiceRunStatus.Complete)
      setContinuePolling(true);

    if (run?.runStatus === InvoiceRunStatus.Complete && !run.generatingExports)
      setContinuePolling(false);

    return () => {
      setContinuePolling(false);
    };
  }, [run]);

  return (
    <Container maxWidth="lg" data-testid="invoiceRunDetailsPage">
      <BreadcrumbBackButton destination="/corporate-accounts" />
      <Paper elevation={2} style={{ padding: '.5rem 1.5rem' }}>
        <Section style={{ marginTop: 0 }} variant="default">
          {run ? (
            <>
              <InvoiceRunDetails run={run} employees={employees} invoiceStatuses={statuses} />
              {corporateAccounts && (
                <Section>
                  <TabContext value={tab}>
                    <Tabs
                      value={tab}
                      onChange={(_, x) => setTab(x)}
                      indicatorColor="primary"
                      textColor="primary"
                      style={{ backgroundColor: AppColors.LighterGray }}
                    >
                      <Tab label="Invoices" value="list" />
                      <Tab
                        label="Send Invoices"
                        value="send"
                        disabled={run.runStatus !== 'Complete'}
                      />
                      <Tab label="Audit Logs" value="audit" />
                    </Tabs>
                    <TabPanel value="list" style={{ padding: 0 }}>
                      <InvoiceStatusFilterForm
                        onChange={onFilterChange}
                        corporateAccounts={corporateAccounts}
                        defaultSort={InvoiceSort.Status}
                      />
                      <InvoiceStatusList
                        statuses={statuses}
                        runId={runId}
                        draft={run.isDraft ?? false}
                        runComplete={run.runStatus === InvoiceRunStatus.Complete}
                      />
                    </TabPanel>
                    <TabPanel value="send" style={{ padding: 0 }}>
                      <SendInvoicesForm
                        runId={run.id ?? ''}
                        isDraft={run.isDraft ?? false}
                        invoices={statuses.filter(
                          s =>
                            s.createInvoiceStatus === 'Success' ||
                            s.createInvoiceStatus === 'SuccessWithWarnings'
                        )}
                      />
                    </TabPanel>
                    <TabPanel value="audit" style={{ padding: 0 }}>
                      <Section title="Audit Logs" disableGutters>
                        <AuditLogs scope="invoice-run" id={run.id} />
                      </Section>
                    </TabPanel>
                  </TabContext>
                </Section>
              )}
            </>
          ) : (
            <Loading />
          )}
        </Section>
      </Paper>
    </Container>
  );
}
